/**
 * Diagnostic logging for the order chat / notification websockets and the order actions that depend on
 * them. Every line starts with `[chat-debug]` so it can be filtered in the browser console (use remote
 * debugging for phones: Safari Web Inspector on iOS, chrome://inspect on Android).
 * Never pass tokens or message contents here; use describeToken / describeError instead.
 */
import { datadogLogs } from '@datadog/browser-logs';

type Level = 'info' | 'warn' | 'error';

let remoteEnabled = false;
let remoteUserId: string | undefined;

// Read directly rather than via localStorageHandler, which pulls in axios; this module stays dependency-free.
function currentUser(): { id?: string; userId?: number } | undefined {
	try {
		return JSON.parse(localStorage.getItem('userInfo') || 'null') ?? undefined;
	} catch {
		return undefined;
	}
}

/**
 * Also send these logs to Datadog Browser Logs when REACT_APP_DATADOG_CLIENT_TOKEN is set for the
 * environment. Without a token (local runs, tests) nothing leaves the browser.
 */
export function initChatDebugRemoteLogging() {
	const env = window.__ENV__;
	const clientToken = env?.REACT_APP_DATADOG_CLIENT_TOKEN;
	if (!clientToken || remoteEnabled) return;
	datadogLogs.init({
		clientToken,
		site: env.REACT_APP_DATADOG_SITE || 'us3.datadoghq.com',
		service: env.REACT_APP_DATADOG_SERVICE || 'p2p-v1-fe',
		env: env.REACT_APP_DATADOG_ENV || undefined,
		// Only chatDebug lines are sent; console output and unrelated errors stay local.
		forwardErrorsToLogs: false,
		forwardConsoleLogs: [],
		sessionSampleRate: 100,
	});
	remoteEnabled = true;
}

function sendRemote(level: Level, scope: string, event: string, data?: Record<string, unknown>) {
	if (!remoteEnabled) return;
	try {
		// Tag logs with the account so one client's session can be pulled up (e.g. @usr.id:h39798447).
		const user = currentUser();
		const userId = user?.id;
		if (userId !== remoteUserId) {
			if (userId) datadogLogs.setUser({ id: userId, userId: user?.userId });
			else datadogLogs.clearUser();
			remoteUserId = userId;
		}
		// Nested under `chat`: fields like `status` would otherwise collide with Datadog's reserved attributes.
		datadogLogs.logger[level](`[${scope}] ${event}`, { chat: { scope, event, ...data } });
	} catch {
		// logging must never break the app
	}
}

function write(level: Level, scope: string, event: string, data?: Record<string, unknown>) {
	const line = `[chat-debug] ${new Date().toISOString()} [${scope}] ${event}`;
	if (data === undefined) console[level](line);
	else console[level](line, data);
	sendRemote(level, scope, event, data);
}

export const chatDebug = {
	info: (scope: string, event: string, data?: Record<string, unknown>) => write('info', scope, event, data),
	warn: (scope: string, event: string, data?: Record<string, unknown>) => write('warn', scope, event, data),
	error: (scope: string, event: string, data?: Record<string, unknown>) => write('error', scope, event, data),
};

/** Expiry details of a JWT without exposing the token itself. */
export function describeToken(token: string): Record<string, unknown> {
	try {
		const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
		if (typeof payload.exp !== 'number') return { length: token.length, exp: 'none' };
		return {
			length: token.length,
			expiresAt: new Date(payload.exp * 1000).toISOString(),
			expiresInSec: Math.round(payload.exp - Date.now() / 1000),
		};
	} catch {
		return { length: token.length, exp: 'undecodable' };
	}
}

/** The useful parts of an axios error, a STOMP frame, or anything else thrown. */
export function describeError(err: any): Record<string, unknown> {
	if (err?.isAxiosError || err?.config) {
		return {
			status: err.response?.status ?? 'no response',
			code: err.response?.data?.code ?? err.code,
			message: err.message,
			url: err.config?.url,
		};
	}
	if (typeof err === 'string') return { message: err };
	if (err?.command) return { frame: err.command, headers: err.headers, body: err.body };
	return { message: err?.message ?? String(err) };
}

/** Current page / network state, attached to connection events. */
export function pageState(): Record<string, unknown> {
	return { visibility: document.visibilityState, online: navigator.onLine };
}
